import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { categories } from '@/mocks/categories';
import DramaCard from '@/components/feature/DramaCard';
import Seo from '@/components/feature/Seo';
import FilterPanel from '@/pages/category/components/FilterPanel';

type SortKey = 'update' | 'heat' | 'rating';

const decadeOptions = ['2020年代', '2010年代', '2000年代'];
const regionOptions = ['中国大陆', '中国香港', '中国台湾', '日韩', '欧美'];
const genreOptions = categories.map((category) => category.name);
const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'update', label: '按更新时间' },
  { key: 'heat', label: '按热度' },
  { key: 'rating', label: '按评分' },
];
const PAGE_SIZE = 6;

export default function Category() {
  const [searchParams] = useSearchParams();
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedDecades, setSelectedDecades] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('update');
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const genreParam = searchParams.get('genre');

  useEffect(() => {
    if (genreParam && genreOptions.includes(genreParam)) {
      setSelectedGenres([genreParam]);
    }
    setPage(1);
  }, [genreParam]);

  const handleToggle = (group: 'genre' | 'decade' | 'region', value: string) => {
    const updater = (list: string[]) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    if (group === 'genre') setSelectedGenres(updater);
    if (group === 'decade') setSelectedDecades(updater);
    if (group === 'region') setSelectedRegions(updater);
    setPage(1);
  };

  const handleReset = () => {
    setSelectedGenres([]);
    setSelectedDecades([]);
    setSelectedRegions([]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = dramas.filter((drama) => {
      const genreOk = selectedGenres.length === 0 || drama.genres.some((genre) => selectedGenres.includes(genre));
      const decadeOk = selectedDecades.length === 0 || selectedDecades.includes(drama.decade);
      const regionOk = selectedRegions.length === 0 || selectedRegions.includes(drama.region);
      return genreOk && decadeOk && regionOk;
    });
    return result.sort((a, b) => {
      if (sort === 'heat') return b.heat - a.heat;
      if (sort === 'rating') return b.rating - a.rating;
      return a.updateTime < b.updateTime ? 1 : -1;
    });
  }, [selectedGenres, selectedDecades, selectedRegions, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const headingText = selectedGenres.length === 1 ? `${selectedGenres[0]}精选` : '电视剧分类';

  return (
    <div className="w-full">
      <Seo
        title={`${headingText} - 上司的老婆影视剧内容站`}
        description={`${headingText}列表，精选${selectedGenres.length === 1 ? selectedGenres[0] : '各类型'}电视剧的剧情介绍、主演信息与更新状态，按年代与地区多维筛选，快速找到想看的剧集。`}
        keywords={`${headingText},电视剧分类,剧情介绍,剧集筛选,${selectedGenres[0] || '电视剧'}`}
      />

      <div className="px-4 md:px-6 lg:px-10 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">电视剧分类</span>
        </nav>
      </div>

      <section className="px-4 md:px-6 lg:px-10 pt-5 pb-10 md:pb-14">
        <div className="mb-6">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{headingText}</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-3xl leading-relaxed">
            浏览平台收录的全部电视剧，按类型、年代与地区自由组合筛选，也可以切换排序方式，快速定位你想了解剧情与角色的那部剧。
          </p>
        </div>

        <div className="hidden lg:block">
          <FilterPanel
            genreOptions={genreOptions}
            decadeOptions={decadeOptions}
            regionOptions={regionOptions}
            selectedGenres={selectedGenres}
            selectedDecades={selectedDecades}
            selectedRegions={selectedRegions}
            onToggle={handleToggle}
            onReset={handleReset}
          />
        </div>

        <div className="lg:hidden flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => setMobileFilterOpen(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 cursor-pointer"
          >
            <i className="ri-filter-3-line"></i>
            筛选条件
          </button>
          <span className="text-xs text-foreground-500 whitespace-nowrap">共 {filtered.length} 部</span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-5">
          <div className="hidden lg:flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-list-check-2"></i>
            共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 部剧集
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar w-full lg:w-auto">
            <span className="text-sm text-foreground-500 shrink-0">排序</span>
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => { setSort(option.key); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === option.key ? 'bg-secondary-900 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                {option.label.replace('按', '')}
              </button>
            ))}
          </div>
        </div>

        {paged.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {paged.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-900">没有找到符合条件的剧集</h2>
            <p className="text-sm text-foreground-500 mt-2 max-w-md">试试减少筛选条件，或直接重置筛选，浏览全部剧集。</p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}

        {totalPages > 1 ? (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              type="button"
              onClick={() => setPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                  pageNumber === currentPage
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        ) : null}
      </section>

      {mobileFilterOpen ? (
        <div className="fixed inset-0 z-[60] lg:hidden flex flex-col bg-background-50 animate-fade-in">
          <div className="flex items-center justify-between px-4 h-16 border-b border-background-200">
            <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              aria-label="关闭筛选"
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer text-xl"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <FilterPanel
              genreOptions={genreOptions}
              decadeOptions={decadeOptions}
              regionOptions={regionOptions}
              selectedGenres={selectedGenres}
              selectedDecades={selectedDecades}
              selectedRegions={selectedRegions}
              onToggle={handleToggle}
              onReset={handleReset}
            />
          </div>
          <div className="px-4 py-4 border-t border-background-200">
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}